<script lang="ts">
  import { onDestroy, tick } from 'svelte';
  import type { Conversation, Message } from '../../shared/types';
  import { del, get, post } from '../api';
  import { CallController } from '../call.svelte';
  import { backDragOffset, isBackSwipe, isHorizontalDrag } from '../gestures';
  import { isOwnMessage } from '../ownership';
  import { forgetRoomOwner, roomOwnerKey } from '../roomOwners';
  import { chat } from '../store';
  import { dayLabel, PLATFORM_LABEL } from '../ui';
  import Avatar from './Avatar.svelte';
  import CallPanel from './CallPanel.svelte';
  import Icon from './Icon.svelte';
  import MessageRow from './MessageRow.svelte';

  const EMPTY: Message[] = [];
  // Matches --anim in styles.css so the DOM swap lands when the slide finishes.
  const ANIM_MS = 377;

  let { conversation }: { conversation: Conversation } = $props();

  const messages = $derived($chat.messages[conversation.id] ?? EMPTY);
  const me = $derived($chat.me);
  const capabilities = $derived($chat.capabilities);

  let draft = $state('');
  let replyTo = $state<Message | null>(null);
  let showCall = $state(false);
  let confirmDelete = $state(false);
  let deleting = $state(false);
  let slideX = $state(0);
  let closing = $state(false);
  let listEl = $state<HTMLDivElement | undefined>();

  let stick = true;
  let closeTimer: number | null = null;
  let swipeStart: { x: number; y: number } | null = null;
  let backAxis: boolean | null = null;

  const owned = conversation.platform === 'mesh' && Boolean(roomOwnerKey(conversation.id));
  const call = new CallController(conversation.id);

  onDestroy(() => {
    void call.destroy();
    if (closeTimer !== null) window.clearTimeout(closeTimer);
  });

  $effect(() => {
    const id = conversation.id;
    let cancelled = false;
    get<{ messages: Message[] }>(`/api/conversations/${encodeURIComponent(id)}/messages`)
      .then((result) => {
        if (!cancelled) chat.setMessages(id, result.messages);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  });

  const lastText = $derived(messages.length ? messages[messages.length - 1].text : '');

  $effect(() => {
    const count = messages.length;
    const text = lastText;
    void count;
    void text;
    void conversation.id;
    void tick().then(() => {
      const element = listEl;
      if (element && stick) element.scrollTop = element.scrollHeight;
    });
  });

  const grouped = $derived(
    messages.map((message, index) => {
      const previous = messages[index - 1];
      return Boolean(
        previous && previous.author.id === message.author.id && message.ts - previous.ts < 5 * 60 * 1000 && !previous.replyPreview,
      );
    }),
  );

  function onScroll() {
    const element = listEl;
    if (!element) return;
    stick = element.scrollHeight - element.scrollTop - element.clientHeight < 90;
  }

  function closeWithSlide() {
    if (closeTimer !== null) return;
    slideX = 0;
    closing = true;
    closeTimer = window.setTimeout(() => chat.setActive(null), ANIM_MS);
  }

  async function send() {
    const text = draft.trim();
    if (!text) return;
    const target = replyTo;
    draft = '';
    replyTo = null;
    stick = true;
    try {
      await post(`/api/conversations/${encodeURIComponent(conversation.id)}/messages`, {
        text,
        replyToId: target?.id,
        authorId: me.id,
        authorName: me.name || undefined,
      });
    } catch (error) {
      chat.notify((error as Error).message, 'error');
    }
  }

  async function startCall(withVideo: boolean) {
    if (!capabilities?.livekit.enabled) {
      chat.notify('Add LiveKit credentials in Settings to start a call', 'error');
      return;
    }
    showCall = true;
    await call.join();
    if (withVideo) await call.toggleCamera();
  }

  async function hangUp() {
    await call.leave();
    showCall = false;
  }

  async function copyInvite() {
    if (!conversation.invite) return;
    try {
      await navigator.clipboard.writeText(conversation.invite);
      chat.notify('Mesh invite copied');
    } catch {
      chat.notify(conversation.invite);
    }
  }

  async function deleteRoom() {
    const ownerKey = roomOwnerKey(conversation.id);
    if (!ownerKey) {
      confirmDelete = false;
      chat.notify('Only the room owner can delete this room', 'error');
      return;
    }
    deleting = true;
    try {
      await del(`/api/mesh/rooms/${encodeURIComponent(conversation.remoteId)}`, {
        headers: { 'x-chatsi-owner-key': ownerKey },
      });
      forgetRoomOwner(conversation.id);
      chat.removeConversation(conversation.id);
      chat.setActive(null);
      chat.notify('Room deleted');
    } catch (error) {
      chat.notify((error as Error).message, 'error');
    } finally {
      deleting = false;
      confirmDelete = false;
    }
  }

  const subtitle = [
    PLATFORM_LABEL[conversation.platform],
    conversation.kind === 'dm' ? 'direct' : conversation.kind,
    conversation.platform === 'mesh' && conversation.peers ? `${conversation.peers} peers` : null,
    owned ? 'owner' : null,
    conversation.meta?.guild,
  ]
    .filter(Boolean)
    .join(' · ');

  function onListTouchStart(event: TouchEvent) {
    if (closing) return;
    const touch = event.touches[0];
    swipeStart = { x: touch.clientX, y: touch.clientY };
    backAxis = null;
  }

  function onListTouchMove(event: TouchEvent) {
    const origin = swipeStart;
    if (!origin) return;
    const touch = event.touches[0];
    const dx = touch.clientX - origin.x;
    const dy = touch.clientY - origin.y;
    if (backAxis === null) {
      if (!isHorizontalDrag(dx, dy)) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) backAxis = false;
        return;
      }
      backAxis = true;
    }
    if (!backAxis) return;
    slideX = backDragOffset(dx, window.innerWidth);
  }

  function onListTouchEnd(event: TouchEvent) {
    const origin = swipeStart;
    swipeStart = null;
    backAxis = null;
    if (!origin) return;
    const touch = event.changedTouches[0];
    if (isBackSwipe(touch.clientX - origin.x, touch.clientY - origin.y)) closeWithSlide();
    else slideX = 0;
  }

  function onListTouchCancel() {
    swipeStart = null;
    backAxis = null;
    slideX = 0;
  }
</script>

<section class={`chat ${slideX ? 'swiping' : ''} ${closing ? 'closing' : ''}`} style:transform={slideX ? `translateX(${slideX}px)` : undefined}>
  <header class="chat-head">
    <button class="icon-btn back" onclick={closeWithSlide} aria-label="Back">
      <Icon name="back" />
    </button>
    <Avatar name={conversation.title} url={conversation.avatar} size={40} />
    <div class="chat-title">
      <strong>{conversation.title}</strong>
      <span>
        {subtitle}
        {#if conversation.invite}
          <button class="invite" onclick={copyInvite} title="Copy invite">{conversation.invite.slice(0, 8)}…</button>
        {/if}
      </span>
    </div>
    <div class="chat-actions">
      {#if owned}
        <button class="icon-btn danger" onclick={() => (confirmDelete = true)} title="Delete room" aria-label="Delete room">
          <Icon name="trash" size={18} />
        </button>
      {/if}
      {#if call.status === 'connected'}
        <button class="icon-btn danger" onclick={hangUp} title="Leave call">
          <Icon name="phoneOff" />
        </button>
      {:else}
        <button class="icon-btn" onclick={() => void startCall(false)} title="Voice call">
          <Icon name="phone" />
        </button>
        <button class="icon-btn" onclick={() => void startCall(true)} title="Video call">
          <Icon name="video" />
        </button>
      {/if}
    </div>
  </header>

  {#if showCall || call.status !== 'idle'}
    <CallPanel {call} {conversation} onClose={hangUp} />
  {/if}

  <div
    class="messages"
    bind:this={listEl}
    onscroll={onScroll}
    ontouchstart={onListTouchStart}
    ontouchmove={onListTouchMove}
    ontouchend={onListTouchEnd}
    ontouchcancel={onListTouchCancel}
  >
    <div class="messages-inner">
      {#each messages as message, index (message.id)}
        <div class="msg-block">
          {#if index === 0 || new Date(messages[index - 1].ts).toDateString() !== new Date(message.ts).toDateString()}
            <div class="day">{dayLabel(message.ts)}</div>
          {/if}
          <MessageRow
            {message}
            own={isOwnMessage(message, me.id, capabilities?.mesh.peerId)}
            grouped={grouped[index]}
            onReply={(target) => (replyTo = target)}
          />
        </div>
      {/each}
      {#if !messages.length}
        <div class="empty-chat">No messages yet. Say hello.</div>
      {/if}
    </div>
  </div>

  {#if replyTo}
    <div class="reply-bar">
      <Icon name="reply" size={16} />
      <div>
        <strong>{replyTo.author.name}</strong>
        <span>{replyTo.text.slice(0, 90)}</span>
      </div>
      <button class="icon-btn" onclick={() => (replyTo = null)} aria-label="Cancel reply">×</button>
    </div>
  {/if}

  <footer class="composer">
    <textarea
      bind:value={draft}
      onkeydown={(event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          void send();
        }
      }}
      placeholder="Message"
      aria-label="Message"
      title="Enter to send · Shift+Enter for a new line"
      rows={1}
    ></textarea>
    <button class="send" onclick={() => void send()} disabled={!draft.trim()} aria-label="Send">
      <Icon name="send" size={22} />
    </button>
  </footer>

  {#if confirmDelete}
    <div class="modal-backdrop" onclick={() => (deleting ? null : (confirmDelete = false))} role="presentation">
      <div
        class="modal small"
        role="dialog"
        aria-modal="true"
        aria-label="Delete room"
        onclick={(event) => event.stopPropagation()}
      >
        <header class="modal-head">
          <strong>Delete this room?</strong>
        </header>
        <div class="modal-body">
          <p>
            <strong>{conversation.title}</strong> and its history will be removed from this server. Other peers keep their own
            replicated copies.
          </p>
          <p class="hint">Only the browser holding this room's owner key can delete it. This cannot be undone.</p>
        </div>
        <footer class="modal-foot">
          <button class="btn danger" onclick={() => void deleteRoom()} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete room'}
          </button>
          <button class="btn ghost" onclick={() => (confirmDelete = false)} disabled={deleting}>Cancel</button>
        </footer>
      </div>
    </div>
  {/if}
</section>
